import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Form, Formik, ErrorMessage } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import { tokens } from "../../../theme";
import {
  allCasesView,
  allReportMinorClassifications,
  allReportsMajorClassifications,
  allReportTypes,
} from "../../../data/Axios/queries";
import {
  AllApprovedPayrollQuery,
  AllClosedPostedPayrollQuery,
  AllPayrollQuery,
} from "../../../data/Queries/HRmanagement/leavemanagement";
import {
  accountsmanagement,
  caseManagementUrl,
  clientManagementUrl,
  HRService,
  reportManagementUrl,
  setupManagementUrl,
} from "../../../config";
import { fetchDataEngine, graphqlQuery } from "../../../data/Axios/DynamicService";
import { generateReport } from "../../../data/DynamicTable/DynamicFunctions";

const normalizeReportName = (value = "") =>
  String(value).toLowerCase().replace(/[^a-z0-9]/g, "");

const HR_SERVICE_FALLBACK_URI = "https://matrix.mcb.co.ke/HRService";
const ACCOUNTS_SERVICE_FALLBACK_URI = "https://matrix.mcb.co.ke/accountsmanagement";
const PAYROLL_RUN_QUERY_CANDIDATES = [
  AllClosedPostedPayrollQuery,
  AllApprovedPayrollQuery,
  AllPayrollQuery,
];
const CASE_ACTIVITIES_REPORT_QUERY = `{
  allCaseActivitiesView {
    id
    fileId
    caseTitle
    fileNo
    clientId
    clientName
    activityType
    actionTypeName
    actionTaken
    actionDate
    remarks
    nextStep
    nextBringUpDate
    reminderDays
    status
    courtName
    caseNumber
    judgeName
    appearanceType
    advocateName
    outcome
    nextOption
    nextOptionDate
    nextAction
    nextActionReminderPeriod
    purposeOfAttendance
    createdDate
    updatedDate
    timeTaken
    courtDate
  }
}`;
const FILE_PROGRESS_REPORT_QUERY = `{
  allFileProgressView {
    id
    fileId
    title
    fileNo
    clientId
    individualFirstName
    individualLastName
    corporateCompanyName
    name
    actionTypeName
    actionTaken
    actionDate
    timeTaken
    remarks
    createdDate
    updatedDate
  }
}`;
const FILE_BRING_UPS_REPORT_QUERY = `{
  allFileBringUpsView {
    id
    fileId
    title
    fileNo
    clientId
    individualFirstName
    individualLastName
    corporateCompanyName
    name
    actionTaken
    actionDate
    nextStep
    nextBringUpDate
    reminderDays
    remarks
    status
    createdDate
    updatedDate
  }
}`;
const ALL_ACCOUNTS_REPORT_QUERY = `{
  allAccounts {
    id
    accountNumber
    accountName
    accountType
    balance
    closingBalance
    financialStatementType
    normalBalance
  }
}`;
const FINANCE_CHART_REPORT_QUERY = `{
  allChartOfAccountsView {
    id
    accountNumber
    accountName
    category
    subCategory
    financialStatementType
    normalBalanceType
    balance
    accountType
    isGrouped
    isSummary
  }
}`;

const PAYROLL_LINE_RETURN_FIELDS = [
  "id",
  "payrollRunId",
  "employeeId",
  "employeeName",
  "kraPin",
  "nssfNo",
  "shifNo",
  "helbNo",
  "grossPay",
  "taxablePay",
  "pensionablePay",
  "totalEarnings",
  "totalDeductions",
  "totalStatutoryDeductions",
  "netPay",
  "employerContributions",
];

const PAYROLL_COMPONENT_RETURN_FIELDS = [
  "id",
  "payrollLineId",
  "payrollRunId",
  "employeeId",
  "employeeName",
  "payComponentName",
  "amount",
  "isDeduction",
  "isStatutory",
];

const PAYROLL_REPORT_DEFINITIONS = [
  {
    key: "master_roll",
    title: "Master Roll Report",
    queryField: "payrollMasterRollReport",
    matchers: ["masterroll", "masterrollreport", "masterpayroll", "masterpayrollreport"],
    fields: [
      "payrollRunId",
      "employeeId",
      "employeeName",
      "kraPin",
      "nssfNo",
      "shifNo",
      "helbNo",
      "grossPay",
      "taxablePay",
      "pensionablePay",
      "totalEarnings",
      "totalDeductions",
      "totalStatutoryDeductions",
      "netPay",
      "employerContributions",
    ],
    columns: [
      { key: "employeeId", label: "Employee ID" },
      { key: "employeeName", label: "Employee Name" },
      { key: "kraPin", label: "KRA PIN" },
      { key: "nssfNo", label: "NSSF No" },
      { key: "shifNo", label: "SHIF No" },
      { key: "helbNo", label: "HELB No" },
      { key: "grossPay", label: "Gross Pay", type: "currency", sum: true },
      { key: "taxablePay", label: "Taxable Pay", type: "currency", sum: true },
      { key: "pensionablePay", label: "Pensionable Pay", type: "currency", sum: true },
      { key: "totalEarnings", label: "Total Earnings", type: "currency", sum: true },
      { key: "totalDeductions", label: "Total Deductions", type: "currency", sum: true },
      {
        key: "totalStatutoryDeductions",
        label: "Statutory Deductions",
        type: "currency",
        sum: true,
      },
      { key: "netPay", label: "Net Pay", type: "currency", sum: true },
      {
        key: "employerContributions",
        label: "Employer Contributions",
        type: "currency",
        sum: true,
      },
    ],
  },
  {
    key: "net_pay",
    title: "Net Pay Report",
    queryField: "payrollNetPayReport",
    matchers: ["netpay", "netpayreport"],
    fields: [
      "payrollRunId",
      "employeeId",
      "employeeName",
      "grossPay",
      "totalDeductions",
      "netPay",
    ],
    columns: [
      { key: "employeeId", label: "Employee ID" },
      { key: "employeeName", label: "Employee Name" },
      { key: "grossPay", label: "Gross Pay", type: "currency", sum: true },
      { key: "totalDeductions", label: "Total Deductions", type: "currency", sum: true },
      { key: "netPay", label: "Net Pay", type: "currency", sum: true },
    ],
  },
  {
    key: "statutory",
    title: "Statutory Report",
    queryField: "payrollStatutoryReport",
    matchers: ["statutory", "statutoryreport"],
    fields: ["payrollRunId", "employeeId", "employeeName", "payComponentName", "amount"],
    columns: [
      { key: "employeeId", label: "Employee ID" },
      { key: "employeeName", label: "Employee Name" },
      { key: "payComponentName", label: "Statutory Component" },
      { key: "amount", label: "Amount", type: "currency", sum: true },
    ],
  },
  {
    key: "reconciliation",
    title: "Reconciliation Report",
    queryField: "payrollReconciliationReport",
    matchers: ["reconciliation", "reconciliationreport"],
    fields: [
      "payrollRunId",
      "employeeCount",
      "totalEarnings",
      "totalDeductions",
      "totalStatutoryDeductions",
      "totalNetPay",
      "totalEmployerContributions",
      "expectedNetPay",
      "variance",
    ],
    columns: [
      { key: "payrollRunId", label: "Payroll Run" },
      { key: "employeeCount", label: "Employees", type: "number", sum: true },
      { key: "totalEarnings", label: "Total Earnings", type: "currency", sum: true },
      { key: "totalDeductions", label: "Total Deductions", type: "currency", sum: true },
      {
        key: "totalStatutoryDeductions",
        label: "Statutory Deductions",
        type: "currency",
        sum: true,
      },
      { key: "totalNetPay", label: "Total Net Pay", type: "currency", sum: true },
      {
        key: "totalEmployerContributions",
        label: "Employer Contributions",
        type: "currency",
        sum: true,
      },
      { key: "expectedNetPay", label: "Expected Net Pay", type: "currency", sum: true },
      { key: "variance", label: "Variance", type: "currency", sum: true },
    ],
  },
];

const FILE_STATUS_REPORT_DEFINITIONS = [
  {
    key: "bring_ups",
    title: "Bring Ups Report",
    matchers: [
      "bringups",
      "bringup",
      "bringupsreport",
      "filebringup",
      "filebringupsreport",
    ],
    columns: [
      { key: "caseNumber", label: "Case Number" },
      { key: "fileNumber", label: "File Number" },
      { key: "title", label: "Title" },
      { key: "clientName", label: "Client" },
      { key: "actionDate", label: "Action Date" },
      { key: "nextStep", label: "Next Step" },
      { key: "nextBringUpDate", label: "Next Bring Up Date" },
      { key: "status", label: "Status" },
      { key: "reminderDays", label: "Reminder (Days)", type: "number", sum: true },
      { key: "remarks", label: "Remarks" },
    ],
  },
  {
    key: "file_progress",
    title: "File Progress Report",
    matchers: ["fileprogress", "fileprogressreport", "progressreport"],
    columns: [
      { key: "caseNumber", label: "Case Number" },
      { key: "fileNumber", label: "File Number" },
      { key: "title", label: "Title" },
      { key: "clientName", label: "Client" },
      { key: "activityType", label: "Activity Type" },
      { key: "actionDate", label: "Action Date" },
      { key: "actionTaken", label: "Action Taken" },
      { key: "nextStep", label: "Next Step" },
      { key: "nextBringUpDate", label: "Next Bring Up Date" },
      { key: "timeTaken", label: "Time Taken" },
      { key: "remarks", label: "Remarks" },
    ],
  },
  {
    key: "file_status_summary",
    title: "File Status Report",
    matchers: ["filestatussummary", "filestatussummaryreport", "filestatusreport", "filestatus"],
    columns: [
      { key: "caseNumber", label: "Case Number" },
      { key: "fileNumber", label: "File Number" },
      { key: "title", label: "Title" },
      { key: "clientName", label: "Client" },
      { key: "category", label: "Category" },
      { key: "subCategory", label: "Sub Category" },
      { key: "caseStatus", label: "Status" },
      { key: "dateOpened", label: "Date Opened" },
      { key: "dateClosed", label: "Date Closed" },
      { key: "balance", label: "Balance", type: "currency", sum: true },
    ],
  },
];

const FINANCE_REPORT_DEFINITIONS = [
  {
    key: "income_statement",
    title: "Income Statement",
    matchers: ["incomestatement", "profitandloss", "profitloss", "pnl"],
    columns: [
      { key: "section", label: "Section" },
      { key: "accountNumber", label: "Account Number" },
      { key: "accountName", label: "Account Name" },
      { key: "category", label: "Category" },
      { key: "amount", label: "Amount", type: "currency", sum: true },
    ],
  },
  {
    key: "balance_sheet",
    title: "Balance Sheet",
    matchers: ["balancesheet", "statementoffinancialposition"],
    columns: [
      { key: "section", label: "Section" },
      { key: "accountNumber", label: "Account Number" },
      { key: "accountName", label: "Account Name" },
      { key: "category", label: "Category" },
      { key: "amount", label: "Amount", type: "currency", sum: true },
    ],
  },
  {
    key: "trial_balance",
    title: "Trial Balance",
    matchers: ["trialbalance", "trialbalancereport"],
    columns: [
      { key: "accountNumber", label: "Account Number" },
      { key: "accountName", label: "Account Name" },
      { key: "category", label: "Category" },
      { key: "normalBalanceType", label: "Normal Balance" },
      { key: "debit", label: "Debit", type: "currency", sum: true },
      { key: "credit", label: "Credit", type: "currency", sum: true },
    ],
  },
  {
    key: "financial_statement",
    title: "Financial Statement Summary",
    matchers: ["financialstatement", "financialstatementreport"],
    columns: [
      { key: "metric", label: "Metric" },
      { key: "amount", label: "Amount", type: "currency" },
    ],
  },
];

const getPayrollReportDefinition = (reportName = "") => {
  const normalized = normalizeReportName(reportName);
  return PAYROLL_REPORT_DEFINITIONS.find((definition) =>
    definition.matchers.some((matcher) => normalized.includes(matcher))
  );
};

const getFileStatusReportDefinition = (reportName = "") => {
  const normalized = normalizeReportName(reportName);
  return FILE_STATUS_REPORT_DEFINITIONS.find((definition) =>
    definition.matchers.some((matcher) => normalized.includes(matcher))
  );
};

const getFinanceReportDefinition = (reportName = "") => {
  const normalized = normalizeReportName(reportName);
  return FINANCE_REPORT_DEFINITIONS.find((definition) =>
    definition.matchers.some((matcher) => normalized.includes(matcher))
  );
};

const isFileProgressReportName = (reportName = "") =>
  getFileStatusReportDefinition(reportName)?.key === "file_progress";

const getHrServiceUris = () => {
  const configuredUri = String(HRService?.uri || "").trim();
  const uris = [];

  if (configuredUri) {
    uris.push(configuredUri);
  }

  if (!configuredUri || /localhost|127\.0\.0\.1/i.test(configuredUri)) {
    uris.push(HR_SERVICE_FALLBACK_URI);
  }

  return [...new Set(uris)];
};

const withHrServiceFallback = async (executor) => {
  const uris = getHrServiceUris();
  let lastError = null;

  for (const uri of uris) {
    try {
      return await executor(uri);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to reach HR service.");
};

const getAccountsServiceUris = () => {
  const configuredUri = String(accountsmanagement?.uri || "").trim().replace(/\/+$/, "");
  const uris = [];

  if (configuredUri) {
    uris.push(configuredUri, `${configuredUri}/`, `${configuredUri}/graphql`, `${configuredUri}/graphql/`);
  }

  if (!configuredUri || /localhost|127\.0\.0\.1/i.test(configuredUri)) {
    uris.push(
      ACCOUNTS_SERVICE_FALLBACK_URI,
      `${ACCOUNTS_SERVICE_FALLBACK_URI}/`,
      `${ACCOUNTS_SERVICE_FALLBACK_URI}/graphql`,
      `${ACCOUNTS_SERVICE_FALLBACK_URI}/graphql/`
    );
  }

  return [...new Set(uris.filter(Boolean))];
};

const withAccountsServiceFallback = async (executor) => {
  const uris = getAccountsServiceUris();
  let lastError = null;

  for (const uri of uris) {
    const maxAttempts = 2;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await executor(uri);
      } catch (error) {
        lastError = error;

        const status = Number(error?.response?.status);
        const isRetryableNetworkError =
          !status &&
          /(network error|err_connection_reset|failed to fetch)/i.test(String(error?.message || ""));

        if (!isRetryableNetworkError || attempt === maxAttempts) {
          break;
        }
      }
    }
  }

  throw lastError || new Error("Unable to reach Accounts service.");
};

const queryAccountsDataWithFallback = async (query) =>
  withAccountsServiceFallback((uri) => graphqlQuery(query, uri, { bypassDedupe: true }));

const queryPayrollDataWithFallback = async (query) => {
  return withHrServiceFallback((uri) => graphqlQuery(query, uri, { bypassDedupe: true }));
};

const fetchDataEngineWithFallback = async (queryName, modelName, inputFields, returnFields) => {
  return withHrServiceFallback((uri) =>
    fetchDataEngine(queryName, modelName, inputFields, returnFields, uri)
  );
};

const asNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const isTruthyFlag = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "y";
  }

  return false;
};

const resolveErrorMessage = (error, fallbackMessage) => {
  const graphQLErrors = error?.response?.data?.errors;
  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    const joinedMessages = graphQLErrors
      .map((item) => item?.message)
      .filter(Boolean)
      .join(" | ");

    if (joinedMessages) {
      return joinedMessages;
    }
  }

  if (error?.message) {
    return error.message;
  }

  return fallbackMessage;
};

const fetchPayrollLinesForRun = async (payrollRunId) => {
  const rows = await fetchDataEngineWithFallback(
    "payrollLinePayrollRunId",
    "payrollLine",
    { payrollRunId },
    PAYROLL_LINE_RETURN_FIELDS
  );

  return Array.isArray(rows) ? rows : [];
};

const fetchPayrollComponentRowsForRun = async (payrollLineIds) => {
  if (!Array.isArray(payrollLineIds) || payrollLineIds.length === 0) {
    return [];
  }

  const detailResults = await Promise.allSettled(
    payrollLineIds.map((payrollLineId) =>
      fetchDataEngineWithFallback(
        "payrollDetailsByPayrollineId",
        "payrollLineDetail",
        { payrollLineId },
        PAYROLL_COMPONENT_RETURN_FIELDS
      )
    )
  );

  return detailResults.flatMap((result) => {
    if (result.status !== "fulfilled" || !Array.isArray(result.value)) {
      return [];
    }

    return result.value;
  });
};

const buildPayrollReportRowsFromPayrollLines = async (definition, payrollRunId) => {
  const payrollLines = await fetchPayrollLinesForRun(payrollRunId);

  if (!Array.isArray(payrollLines) || payrollLines.length === 0) {
    return [];
  }

  if (definition.key === "master_roll") {
    return payrollLines;
  }

  if (definition.key === "net_pay") {
    return payrollLines.map((row) => ({
      payrollRunId: row.payrollRunId,
      employeeId: row.employeeId,
      employeeName: row.employeeName,
      grossPay: row.grossPay,
      totalDeductions: row.totalDeductions,
      netPay: row.netPay,
    }));
  }

  if (definition.key === "statutory") {
    const lineIds = payrollLines
      .map((row) => Number.parseInt(row.id, 10))
      .filter((id) => Number.isFinite(id));

    const componentRows = await fetchPayrollComponentRowsForRun(lineIds);
    const statutoryRows = componentRows
      .filter((row) => isTruthyFlag(row.isStatutory) && isTruthyFlag(row.isDeduction))
      .map((row) => ({
        payrollRunId: row.payrollRunId ?? payrollRunId,
        employeeId: row.employeeId,
        employeeName: row.employeeName,
        payComponentName: row.payComponentName,
        amount: row.amount,
      }));

    if (statutoryRows.length > 0) {
      return statutoryRows;
    }

    return payrollLines
      .map((row) => ({
        payrollRunId: row.payrollRunId ?? payrollRunId,
        employeeId: row.employeeId,
        employeeName: row.employeeName,
        payComponentName: "Total Statutory Deductions",
        amount: row.totalStatutoryDeductions ?? 0,
      }))
      .filter((row) => asNumber(row.amount) !== 0);
  }

  if (definition.key === "reconciliation") {
    const totals = payrollLines.reduce(
      (accumulator, row) => ({
        employeeCount: accumulator.employeeCount + 1,
        totalEarnings: accumulator.totalEarnings + asNumber(row.totalEarnings),
        totalDeductions: accumulator.totalDeductions + asNumber(row.totalDeductions),
        totalStatutoryDeductions:
          accumulator.totalStatutoryDeductions + asNumber(row.totalStatutoryDeductions),
        totalNetPay: accumulator.totalNetPay + asNumber(row.netPay),
        totalEmployerContributions:
          accumulator.totalEmployerContributions + asNumber(row.employerContributions),
      }),
      {
        employeeCount: 0,
        totalEarnings: 0,
        totalDeductions: 0,
        totalStatutoryDeductions: 0,
        totalNetPay: 0,
        totalEmployerContributions: 0,
      }
    );

    const expectedNetPay = totals.totalEarnings - totals.totalDeductions;
    const variance = totals.totalNetPay - expectedNetPay;

    return [
      {
        payrollRunId,
        employeeCount: totals.employeeCount,
        totalEarnings: totals.totalEarnings,
        totalDeductions: totals.totalDeductions,
        totalStatutoryDeductions: totals.totalStatutoryDeductions,
        totalNetPay: totals.totalNetPay,
        totalEmployerContributions: totals.totalEmployerContributions,
        expectedNetPay,
        variance,
      },
    ];
  }

  return payrollLines;
};

const normalizeText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const toSearchText = (row = {}) =>
  normalizeText(
    `${row.category || ""} ${row.subCategory || ""} ${row.accountName || ""} ${row.accountType || ""}`
  );

const INCOME_KEYWORDS = [
  "income",
  "revenue",
  "sales",
  "fees",
  "commission",
  "interestincome",
  "otherincome",
];
const EXPENSE_KEYWORDS = [
  "expense",
  "cost",
  "cogs",
  "administrative",
  "operating",
  "overhead",
  "depreciation",
  "amortization",
  "tax",
];
const ASSET_KEYWORDS = [
  "asset",
  "cash",
  "bank",
  "receivable",
  "inventory",
  "prepaid",
  "fixedasset",
  "equipment",
  "land",
  "building",
  "debtors",
];
const LIABILITY_KEYWORDS = [
  "liability",
  "payable",
  "creditor",
  "accrual",
  "loan",
  "taxpayable",
];
const EQUITY_KEYWORDS = ["equity", "capital", "retainedearning", "drawings"];

const containsAnyKeyword = (searchText, keywords) =>
  keywords.some((keyword) => searchText.includes(keyword));

const parseComparableDate = (value) => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
};

const isDateWithinRange = (candidate, startDate, endDate) => {
  const comparableDate = parseComparableDate(candidate);
  if (!comparableDate) {
    return !(startDate || endDate);
  }

  const comparableStart = startDate ? parseComparableDate(startDate) : null;
  const comparableEnd = endDate ? parseComparableDate(endDate) : null;

  if (comparableStart && comparableDate < comparableStart) {
    return false;
  }
  if (comparableEnd && comparableDate > comparableEnd) {
    return false;
  }

  return true;
};

const resolveClientNameForCase = (item = {}) => {
  if (item.corporateCompanyName) {
    return item.corporateCompanyName;
  }

  const firstName = String(item.individualFirstName || "").trim();
  const lastName = String(item.individualLastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    return fullName;
  }

  return item.name || "-";
};

const fetchFileStatusSummaryRows = async (filter = {}) => {
  const caseRows = await graphqlQuery(allCasesView, caseManagementUrl.uri, { bypassDedupe: true });
  const rows = Array.isArray(caseRows) ? caseRows : [];

  const uniqueRows = Array.from(
    new Map(
      rows
        .filter((item) => item && item.id !== null && item.id !== undefined)
        .map((item) => [String(item.id), item])
    ).values()
  );

  const filteredRows = uniqueRows.filter((item) => {
    const candidateDate = item.dateOpened || item.createdDate || item.dateClosed;
    return isDateWithinRange(candidateDate, filter.startDate, filter.endDate);
  });

  return filteredRows
    .map((item) => ({
      caseNumber: item.caseNumber || "-",
      fileNumber: item.fileNumber || "-",
      title: item.title || "-",
      clientName: resolveClientNameForCase(item),
      category: item.categoryName || "-",
      subCategory: item.subcategoryName || "-",
      caseStatus: item.caseStatus || item.statusFlag || "-",
      dateOpened: formatDate(item.dateOpened),
      dateClosed: formatDate(item.dateClosed),
      balance: asNumber(item.balance),
    }))
    .sort((left, right) => {
      const leftDate = new Date(left.dateOpened || 0).getTime();
      const rightDate = new Date(right.dateOpened || 0).getTime();
      return rightDate - leftDate;
    });
};

const mapCaseActivityReportRow = (item = {}, fallbackActivityType = "") => {
  const fallbackClientName = resolveClientNameForCase(item);

  return {
    id: item.id,
    fileId: item.fileId,
    clientId: item.clientId,
    caseNumber: item.caseNumber || "-",
    fileNumber: item.fileNo || item.fileNumber || "-",
    title: item.caseTitle || item.title || "-",
    clientName: item.clientName || item.name || fallbackClientName,
    activityType: item.activityType || fallbackActivityType || item.actionTypeName || "-",
    actionTypeName: item.actionTypeName || "-",
    actionTaken: item.actionTaken || "-",
    actionDate: formatDate(item.actionDate || item.createdDate || item.courtDate),
    nextStep: item.nextStep || "-",
    nextBringUpDate: formatDate(item.nextBringUpDate),
    reminderDays: asNumber(item.reminderDays),
    status: item.status || "-",
    remarks: item.remarks || "-",
    outcome: item.outcome || "-",
    timeTaken: item.timeTaken || "-",
    rawActionDate: item.actionDate || item.createdDate || item.courtDate,
    rawNextBringUpDate: item.nextBringUpDate,
    rawCourtDate: item.courtDate,
    rawCreatedDate: item.createdDate,
    rawUpdatedDate: item.updatedDate,
  };
};

const dedupeRowsByPrimaryKey = (rows = []) =>
  Array.from(
    new Map(
      rows
        .filter(Boolean)
        .map((row) => {
          const key =
            row.id !== undefined && row.id !== null
              ? String(row.id)
              : `${row.fileId || ""}|${row.activityType || ""}|${row.rawActionDate || ""}|${
                  row.rawNextBringUpDate || ""
                }|${row.caseNumber || ""}`;
          return [key, row];
        })
    ).values()
  );

const filterCaseActivityRowsForReports = (rows = [], filter = {}) => {
  const selectedClientId = Number.parseInt(filter.clientId, 10);

  return rows.filter((row) => {
    const candidateDate =
      row.rawActionDate || row.rawNextBringUpDate || row.rawCourtDate || row.rawCreatedDate;

    if (!isDateWithinRange(candidateDate, filter.startDate, filter.endDate)) {
      return false;
    }

    if (!Number.isNaN(selectedClientId) && Number(row.clientId) !== selectedClientId) {
      return false;
    }

    return true;
  });
};

const fetchCaseActivityReportRows = async (filter = {}) => {
  let lastError = null;

  try {
    const activityRows = await graphqlQuery(CASE_ACTIVITIES_REPORT_QUERY, caseManagementUrl.uri, {
      bypassDedupe: true,
    });
    const mappedRows = dedupeRowsByPrimaryKey(
      (Array.isArray(activityRows) ? activityRows : []).map((item) => mapCaseActivityReportRow(item))
    );
    return filterCaseActivityRowsForReports(mappedRows, filter);
  } catch (error) {
    lastError = error;
  }

  const [fileProgressResult, bringUpsResult] = await Promise.allSettled([
    graphqlQuery(FILE_PROGRESS_REPORT_QUERY, caseManagementUrl.uri, { bypassDedupe: true }),
    graphqlQuery(FILE_BRING_UPS_REPORT_QUERY, caseManagementUrl.uri, { bypassDedupe: true }),
  ]);

  const fallbackRows = [
    ...(fileProgressResult.status === "fulfilled" && Array.isArray(fileProgressResult.value)
      ? fileProgressResult.value.map((item) => mapCaseActivityReportRow(item, "File Progress"))
      : []),
    ...(bringUpsResult.status === "fulfilled" && Array.isArray(bringUpsResult.value)
      ? bringUpsResult.value.map((item) => mapCaseActivityReportRow(item, "File Bring Up"))
      : []),
  ];

  if (fallbackRows.length > 0) {
    return filterCaseActivityRowsForReports(dedupeRowsByPrimaryKey(fallbackRows), filter);
  }

  const fallbackError =
    fileProgressResult.status === "rejected"
      ? fileProgressResult.reason
      : bringUpsResult.status === "rejected"
      ? bringUpsResult.reason
      : null;

  throw fallbackError || lastError || new Error("Unable to load file activity report data.");
};

const buildBringUpsReportRows = async (filter = {}) => {
  const activityRows = await fetchCaseActivityReportRows(filter);

  return activityRows
    .filter((row) => {
      const normalizedActivityType = normalizeText(row.activityType || "");
      return normalizedActivityType.includes("bringup") || Boolean(row.rawNextBringUpDate);
    })
    .map((row) => ({
      caseNumber: row.caseNumber,
      fileNumber: row.fileNumber,
      title: row.title,
      clientName: row.clientName,
      actionDate: row.actionDate,
      nextStep: row.nextStep,
      nextBringUpDate: row.nextBringUpDate,
      status: row.status,
      reminderDays: row.reminderDays,
      remarks: row.remarks,
    }))
    .sort((left, right) => {
      const leftDate = new Date(left.nextBringUpDate || left.actionDate || 0).getTime();
      const rightDate = new Date(right.nextBringUpDate || right.actionDate || 0).getTime();
      return leftDate - rightDate;
    });
};

const buildFileProgressReportRows = async (filter = {}) => {
  const activityRows = await fetchCaseActivityReportRows(filter);

  return activityRows
    .filter((row) => {
      const normalizedActivityType = normalizeText(row.activityType || "");
      return normalizedActivityType.includes("progress");
    })
    .map((row) => ({
      caseNumber: row.caseNumber,
      fileNumber: row.fileNumber,
      title: row.title,
      clientName: row.clientName,
      activityType: row.activityType,
      actionDate: row.actionDate,
      actionTaken: row.actionTaken,
      nextStep: row.nextStep,
      nextBringUpDate: row.nextBringUpDate,
      timeTaken: row.timeTaken,
      remarks: row.remarks,
    }))
    .sort((left, right) => {
      const leftDate = new Date(left.actionDate || 0).getTime();
      const rightDate = new Date(right.actionDate || 0).getTime();
      return rightDate - leftDate;
    });
};

const buildFileStatusReportRows = async (definition, filter = {}) => {
  switch (definition.key) {
    case "bring_ups":
      return buildBringUpsReportRows(filter);
    case "file_progress":
      return buildFileProgressReportRows(filter);
    case "file_status_summary":
    default:
      return fetchFileStatusSummaryRows(filter);
  }
};

const resolveNormalBalanceType = (value) => {
  const normalized = normalizeText(value);
  if (normalized.includes("debit") || normalized === "dr" || normalized === "1") {
    return "Debit";
  }
  if (normalized.includes("credit") || normalized === "cr" || normalized === "2") {
    return "Credit";
  }

  const numeric = Number(value);
  if (numeric === 1) {
    return "Debit";
  }
  if (numeric === 2) {
    return "Credit";
  }

  return "";
};

const normalizeChartRows = (rows = []) => {
  const uniqueRows = Array.from(
    new Map(
      rows
        .filter((item) => item && item.id !== null && item.id !== undefined)
        .map((item) => [String(item.id), item])
    ).values()
  );

  const detailRows = uniqueRows.filter(
    (item) => Number(item.isSummary) !== 1 && Number(item.isGrouped) !== 1
  );

  return detailRows.length > 0 ? detailRows : uniqueRows;
};

const mapAllAccountsToChartRows = (rows = []) =>
  rows
    .filter((item) => item && item.id !== null && item.id !== undefined)
    .map((item) => ({
      id: item.id,
      accountNumber: item.accountNumber,
      accountName: item.accountName,
      category: item.accountType || item.status || "-",
      subCategory: "",
      financialStatementType: item.financialStatementType,
      normalBalanceType: resolveNormalBalanceType(item.normalBalanceType ?? item.normalBalance),
      balance: item.closingBalance ?? item.balance ?? 0,
      isGrouped: 0,
      isSummary: 0,
    }));

const fetchChartOfAccountsForReports = async () => {
  let lastError = null;

  try {
    const chartRows = await queryAccountsDataWithFallback(FINANCE_CHART_REPORT_QUERY);
    const normalizedRows = normalizeChartRows(Array.isArray(chartRows) ? chartRows : []);
    if (normalizedRows.length > 0) {
      return normalizedRows;
    }
  } catch (error) {
    lastError = error;
  }

  try {
    const accountRows = await queryAccountsDataWithFallback(ALL_ACCOUNTS_REPORT_QUERY);
    const mappedRows = mapAllAccountsToChartRows(Array.isArray(accountRows) ? accountRows : []);
    if (mappedRows.length > 0) {
      return mappedRows;
    }
  } catch (error) {
    lastError = error;
  }

  throw lastError || new Error("Unable to load chart of accounts for finance reports.");
};

const resolveIncomeSection = (row = {}) => {
  const searchText = toSearchText(row);
  const normalizedBalanceType = normalizeText(row.normalBalanceType || "");
  const statementType = Number(row.financialStatementType);

  if (containsAnyKeyword(searchText, INCOME_KEYWORDS)) {
    return "Revenue";
  }
  if (containsAnyKeyword(searchText, EXPENSE_KEYWORDS)) {
    return "Expense";
  }

  if (statementType === 1) {
    if (normalizedBalanceType.includes("credit")) {
      return "Revenue";
    }
    if (normalizedBalanceType.includes("debit")) {
      return "Expense";
    }
  }

  return null;
};

const resolveBalanceSheetSection = (row = {}) => {
  const searchText = toSearchText(row);
  const normalizedBalanceType = normalizeText(row.normalBalanceType || "");
  const statementType = Number(row.financialStatementType);

  if (containsAnyKeyword(searchText, ASSET_KEYWORDS)) {
    return "Assets";
  }
  if (containsAnyKeyword(searchText, LIABILITY_KEYWORDS)) {
    return "Liabilities";
  }
  if (containsAnyKeyword(searchText, EQUITY_KEYWORDS)) {
    return "Equity";
  }

  if (statementType === 2) {
    if (normalizedBalanceType.includes("debit")) {
      return "Assets";
    }
    if (normalizedBalanceType.includes("credit")) {
      return "Liabilities/Equity";
    }
  }

  return null;
};

const buildIncomeStatementRows = (chartRows) => {
  return chartRows
    .map((row) => {
      const section = resolveIncomeSection(row);
      if (!section) {
        return null;
      }

      const absoluteBalance = Math.abs(asNumber(row.balance));
      const signedAmount = section === "Expense" ? -absoluteBalance : absoluteBalance;

      return {
        section,
        accountNumber: row.accountNumber || "-",
        accountName: row.accountName || "-",
        category: row.category || "-",
        amount: signedAmount,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (left.section !== right.section) {
        return String(left.section).localeCompare(String(right.section));
      }
      return String(left.accountNumber || "").localeCompare(String(right.accountNumber || ""));
    });
};

const buildBalanceSheetRows = (chartRows) => {
  return chartRows
    .map((row) => {
      const section = resolveBalanceSheetSection(row);
      if (!section) {
        return null;
      }

      return {
        section,
        accountNumber: row.accountNumber || "-",
        accountName: row.accountName || "-",
        category: row.category || "-",
        amount: Math.abs(asNumber(row.balance)),
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      if (left.section !== right.section) {
        return String(left.section).localeCompare(String(right.section));
      }
      return String(left.accountNumber || "").localeCompare(String(right.accountNumber || ""));
    });
};

const buildTrialBalanceRows = (chartRows) => {
  return chartRows
    .map((row) => {
      const normalizedBalanceType = normalizeText(row.normalBalanceType || "");
      const absoluteBalance = Math.abs(asNumber(row.balance));
      let debit = 0;
      let credit = 0;

      if (normalizedBalanceType.includes("debit") || normalizedBalanceType === "dr") {
        debit = absoluteBalance;
      } else if (normalizedBalanceType.includes("credit") || normalizedBalanceType === "cr") {
        credit = absoluteBalance;
      } else if (asNumber(row.balance) >= 0) {
        debit = absoluteBalance;
      } else {
        credit = absoluteBalance;
      }

      return {
        accountNumber: row.accountNumber || "-",
        accountName: row.accountName || "-",
        category: row.category || "-",
        normalBalanceType: row.normalBalanceType || "-",
        debit,
        credit,
      };
    })
    .sort((left, right) =>
      String(left.accountNumber || "").localeCompare(String(right.accountNumber || ""))
    );
};

const buildFinancialStatementSummaryRows = (chartRows) => {
  const incomeRows = buildIncomeStatementRows(chartRows);
  const balanceSheetRows = buildBalanceSheetRows(chartRows);

  const totalRevenue = incomeRows
    .filter((row) => row.section === "Revenue")
    .reduce((total, row) => total + asNumber(row.amount), 0);
  const totalExpenses = Math.abs(
    incomeRows
      .filter((row) => row.section === "Expense")
      .reduce((total, row) => total + asNumber(row.amount), 0)
  );
  const netProfitOrLoss = totalRevenue - totalExpenses;

  const totalAssets = balanceSheetRows
    .filter((row) => row.section === "Assets")
    .reduce((total, row) => total + asNumber(row.amount), 0);
  const totalLiabilities = balanceSheetRows
    .filter((row) => row.section === "Liabilities" || row.section === "Liabilities/Equity")
    .reduce((total, row) => total + asNumber(row.amount), 0);
  const totalEquity = balanceSheetRows
    .filter((row) => row.section === "Equity")
    .reduce((total, row) => total + asNumber(row.amount), 0);

  return [
    { metric: "Total Revenue", amount: totalRevenue },
    { metric: "Total Expenses", amount: totalExpenses },
    { metric: "Net Profit/(Loss)", amount: netProfitOrLoss },
    { metric: "Total Assets", amount: totalAssets },
    { metric: "Total Liabilities", amount: totalLiabilities },
    { metric: "Total Equity", amount: totalEquity },
    { metric: "Liabilities + Equity", amount: totalLiabilities + totalEquity },
    { metric: "Balance Variance (Assets - Liabilities - Equity)", amount: totalAssets - (totalLiabilities + totalEquity) },
  ];
};

const buildFinanceReportRows = async (definition) => {
  const chartRows = await fetchChartOfAccountsForReports();

  switch (definition.key) {
    case "income_statement":
      return buildIncomeStatementRows(chartRows);
    case "balance_sheet":
      return buildBalanceSheetRows(chartRows);
    case "trial_balance":
      return buildTrialBalanceRows(chartRows);
    case "financial_statement":
      return buildFinancialStatementSummaryRows(chartRows);
    default:
      return [];
  }
};

const normalizePayrollRuns = (runs) => {
  if (!Array.isArray(runs)) {
    return [];
  }

  const uniqueRuns = Array.from(
    new Map(
      runs
        .filter((run) => run && run.id !== undefined && run.id !== null)
        .map((run) => [String(run.id), run])
    ).values()
  );

  return uniqueRuns.sort((left, right) => {
    const leftDate = new Date(left.periodEnd || left.periodStart || 0).getTime();
    const rightDate = new Date(right.periodEnd || right.periodStart || 0).getTime();

    if (rightDate !== leftDate) {
      return rightDate - leftDate;
    }

    return Number(right.id || 0) - Number(left.id || 0);
  });
};

const fetchPayrollRunsWithFallback = async () => {
  const uris = getHrServiceUris();
  let lastError = null;
  let hadSuccessfulResponse = false;

  for (const uri of uris) {
    for (const payrollQuery of PAYROLL_RUN_QUERY_CANDIDATES) {
      try {
        const response = await graphqlQuery(payrollQuery, uri, { bypassDedupe: true });
        if (Array.isArray(response)) {
          hadSuccessfulResponse = true;
          if (response.length > 0) {
            return normalizePayrollRuns(response);
          }
        }
      } catch (error) {
        lastError = error;
      }
    }
  }

  if (hadSuccessfulResponse) {
    return [];
  }

  throw lastError || new Error("Unable to load payroll runs.");
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatCurrency = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatNumber = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  return numericValue.toLocaleString("en-US");
};

const formatDate = (date) => {
  if (!date) {
    return "";
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLastDayOfMonth = (dateString) => {
  if (!dateString) {
    return "";
  }

  const inputDate = new Date(dateString);
  if (Number.isNaN(inputDate.getTime())) {
    return "";
  }

  const lastDay = new Date(inputDate.getFullYear(), inputDate.getMonth() + 1, 0);
  return formatDate(lastDay);
};

const sanitizeReportFileName = (value = "Report") =>
  String(value || "Report")
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "-")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "") || "Report";

const escapeJsString = (value = "") =>
  String(value)
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'")
    .replace(/\r?\n/g, " ");

const buildReportActionsMarkup = () => `
<div class="sp-report-actions">
  <button type="button" onclick="downloadReportPdf()">Download PDF</button>
  <button type="button" onclick="downloadReportExcel()">Download Excel</button>
  <button type="button" onclick="window.print()">Print</button>
</div>`;

const buildReportActionsStyles = () => `
<style>
  .sp-report-actions {
    margin-bottom: 14px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
  }

  .sp-report-actions button {
    padding: 8px 14px;
    border: 1px solid #1f3f77;
    background: #1f3f77;
    color: #fff;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
  }

  .sp-report-actions button:hover {
    background: #17315d;
  }

  @media print {
    .sp-report-actions {
      display: none !important;
    }
  }
</style>`;

const buildReportActionsScript = (fileNameBase, targetSelector = "body") => `
<script>
(() => {
  const fileBaseName = '${escapeJsString(fileNameBase || "Report")}';
  const contentSelector = '${escapeJsString(targetSelector || "body")}';

  const triggerDownload = (blob, fileName) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === "true") {
          resolve();
          return;
        }
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Failed to load script: " + src)), {
          once: true,
        });
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => {
        script.dataset.loaded = "true";
        resolve();
      };
      script.onerror = () => reject(new Error("Failed to load script: " + src));
      document.head.appendChild(script);
    });

  window.downloadReportExcel = () => {
    try {
      const table = document.querySelector("table");
      if (!table) {
        alert("No table data found to export.");
        return;
      }

      const workbook = '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
        'xmlns:x="urn:schemas-microsoft-com:office:excel" ' +
        'xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8"></head>' +
        '<body>' + table.outerHTML + '</body></html>';
      const blob = new Blob(['\\ufeff', workbook], {
        type: "application/vnd.ms-excel;charset=utf-8;",
      });
      triggerDownload(blob, fileBaseName + ".xls");
    } catch (error) {
      console.error("Excel download failed:", error);
      alert("Unable to download Excel. Please try again.");
    }
  };

  window.downloadReportPdf = async () => {
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

      const reportRoot = document.querySelector(contentSelector) || document.body;
      const canvas = await window.html2canvas(reportRoot, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const jspdfNamespace = window.jspdf || {};
      const jsPDF = jspdfNamespace.jsPDF;
      if (!jsPDF) {
        throw new Error("jsPDF library failed to load.");
      }

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const imageData = canvas.toDataURL("image/png");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imageHeight = (canvas.height * pageWidth) / canvas.width;

      let remainingHeight = imageHeight;
      let position = 0;
      pdf.addImage(imageData, "PNG", 0, position, pageWidth, imageHeight, undefined, "FAST");
      remainingHeight -= pageHeight;

      while (remainingHeight > 0) {
        position = remainingHeight - imageHeight;
        pdf.addPage();
        pdf.addImage(imageData, "PNG", 0, position, pageWidth, imageHeight, undefined, "FAST");
        remainingHeight -= pageHeight;
      }

      pdf.save(fileBaseName + ".pdf");
    } catch (error) {
      console.error("PDF download failed:", error);
      window.print();
    }
  };
})();
</script>`;

const addReportActionsToHtml = (htmlContent, reportName) => {
  if (!htmlContent || typeof htmlContent !== "string") {
    return htmlContent;
  }

  const fileName = sanitizeReportFileName(reportName || "Report");
  const actionsMarkup = buildReportActionsMarkup();
  const actionsStyles = buildReportActionsStyles();
  const actionsScript = buildReportActionsScript(fileName, "#sp-report-content");

  let html = htmlContent;
  if (!/<html[\s>]/i.test(html)) {
    html = `<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body>${html}</body></html>`;
  }

  if (!/id=["']sp-report-content["']/i.test(html)) {
    if (/<body[^>]*>/i.test(html) && /<\/body>/i.test(html)) {
      html = html.replace(/<body([^>]*)>/i, "<body$1><div id=\"sp-report-content\">");
      html = html.replace(/<\/body>/i, "</div></body>");
    }
  }

  if (!/class=["']sp-report-actions["']/i.test(html)) {
    if (/<body[^>]*>/i.test(html)) {
      html = html.replace(/<body[^>]*>/i, (match) => `${match}${actionsMarkup}`);
    } else {
      html = `${actionsMarkup}${html}`;
    }
  }

  if (/<\/head>/i.test(html)) {
    html = html.replace(/<\/head>/i, `${actionsStyles}${actionsScript}</head>`);
  } else if (/<html[^>]*>/i.test(html)) {
    html = html.replace(
      /<html([^>]*)>/i,
      `<html$1><head><meta charset="utf-8" />${actionsStyles}${actionsScript}</head>`
    );
  } else {
    html = `${actionsStyles}${actionsScript}${html}`;
  }

  return html;
};

const openLoadingReportTab = () => {
  const newTab = window.open("", "_blank");
  if (!newTab) {
    return null;
  }

  newTab.document.write(`
    <html>
      <head>
        <title>Loading report...</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            margin: 0;
            background-color: #f4f6f8;
            color: #1d2838;
          }
          .loading {
            font-size: 22px;
            font-weight: 600;
          }
        </style>
      </head>
      <body>
        <div class="loading">Generating report...</div>
      </body>
    </html>
  `);
  newTab.document.close();

  return newTab;
};

const buildPayrollReportQuery = (definition, filter = {}) => {
  const filterEntries = [];

  if (Number.isFinite(filter.payrollRunId)) {
    filterEntries.push(`payrollRunId: ${filter.payrollRunId}`);
  }

  const filterArgument = filterEntries.length
    ? `(filter: { ${filterEntries.join(", ")} })`
    : "";

  return `
{
  ${definition.queryField}${filterArgument} {
    ${definition.fields.join("\n    ")}
  }
}
`;
};

const buildPayrollReportHtml = (definition, rows, metadata) => {
  const { columns, title } = definition;
  const fileNameBase = sanitizeReportFileName(metadata.reportName || title || "Report");
  const totals = {};
  columns.forEach((column) => {
    if (column.sum) {
      totals[column.key] = 0;
    }
  });

  rows.forEach((row) => {
    columns.forEach((column) => {
      if (!column.sum) {
        return;
      }

      const numericValue = Number(row[column.key] ?? 0);
      if (Number.isFinite(numericValue)) {
        totals[column.key] += numericValue;
      }
    });
  });

  const tableHeader = columns
    .map((column) => `<th>${escapeHtml(column.label)}</th>`)
    .join("");

  const tableRows = rows.length
    ? rows
        .map((row) => {
          const cells = columns
            .map((column) => {
              const rawValue = row[column.key];
              if (column.type === "currency") {
                return `<td class="numeric">${formatCurrency(rawValue)}</td>`;
              }
              if (column.type === "number") {
                return `<td class="numeric">${formatNumber(rawValue)}</td>`;
              }
              return `<td>${escapeHtml(rawValue ?? "-")}</td>`;
            })
            .join("");

          return `<tr>${cells}</tr>`;
        })
        .join("")
    : `<tr><td class="empty" colspan="${columns.length}">No data found for the selected filters.</td></tr>`;

  const totalsRow = rows.length
    ? `<tr class="total-row">${columns
        .map((column, index) => {
          if (index === 0) {
            return "<td>Total</td>";
          }
          if (!column.sum) {
            return "<td></td>";
          }
          if (column.type === "currency") {
            return `<td class="numeric">${formatCurrency(totals[column.key])}</td>`;
          }
          if (column.type === "number") {
            return `<td class="numeric">${formatNumber(totals[column.key])}</td>`;
          }
          return `<td>${escapeHtml(totals[column.key])}</td>`;
        })
        .join("")}</tr>`
    : "";

  const generatedAt = new Date().toLocaleString();
  const metadataRows = [
    metadata.reportName ? `<div><strong>Report Type:</strong> ${escapeHtml(metadata.reportName)}</div>` : "",
    metadata.payrollRunLabel
      ? `<div><strong>Payroll Run:</strong> ${escapeHtml(metadata.payrollRunLabel)}</div>`
      : "",
    metadata.startDate ? `<div><strong>Start Date:</strong> ${escapeHtml(metadata.startDate)}</div>` : "",
    metadata.endDate ? `<div><strong>End Date:</strong> ${escapeHtml(metadata.endDate)}</div>` : "",
    `<div><strong>Generated At:</strong> ${escapeHtml(generatedAt)}</div>`,
  ]
    .filter(Boolean)
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 24px;
      color: #202124;
    }
    #sp-report-content {
      width: 100%;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 16px;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
    }
    .meta {
      margin: 0 0 16px;
      font-size: 13px;
      color: #334155;
      line-height: 1.6;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    th, td {
      border: 1px solid #d9e1ec;
      padding: 8px;
      text-align: left;
      vertical-align: top;
    }
    th {
      background-color: #eff4fb;
      font-weight: 700;
    }
    td.numeric {
      text-align: right;
      white-space: nowrap;
    }
    tr.total-row td {
      font-weight: 700;
      background-color: #f8fafc;
    }
    td.empty {
      text-align: center;
      color: #64748b;
      font-style: italic;
      padding: 16px;
    }
    @media print {
      body { margin: 8px; }
    }
  </style>
  ${buildReportActionsStyles()}
  ${buildReportActionsScript(fileNameBase, "#sp-report-content")}
</head>
<body>
  ${buildReportActionsMarkup()}
  <div id="sp-report-content">
    <div class="header">
      <h1>${escapeHtml(title)}</h1>
    </div>
    <div class="meta">${metadataRows}</div>
    <table>
      <thead>
        <tr>${tableHeader}</tr>
      </thead>
      <tbody>
        ${tableRows}
        ${totalsRow}
      </tbody>
    </table>
  </div>
</body>
</html>`;
};

const Filter = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // --- Major classifications: fetched once, immediately on mount (needed for the first field) ---
  const [majorLoading, setMajorLoading] = useState(true);
  const [major, setMajor] = useState([]);

  // --- Everything else: fetched lazily, the first time it's actually needed ---
  const [minor, setMinor] = useState([]);
  const [minorLoaded, setMinorLoaded] = useState(false);
  const [minorLoading, setMinorLoading] = useState(false);

  const [reportTypes, setReportTypes] = useState([]);
  const [reportTypesLoaded, setReportTypesLoaded] = useState(false);
  const [reportTypesLoading, setReportTypesLoading] = useState(false);

  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);

  const [payrollRuns, setPayrollRuns] = useState([]);
  const [payrollRunsLoaded, setPayrollRunsLoaded] = useState(false);
  const [payrollRunsLoading, setPayrollRunsLoading] = useState(false);

  const [filteredMinorOptions, setFilteredMinorOptions] = useState([]);
  const [filteredReportTypesOptions, setFilteredReportTypesOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = Number.parseInt(decodedToken.Id, 10) || 0;
  const companyId = Number.parseInt(decodedToken.CompanyId, 10) || 0;

  // Only the major classification list loads on mount. Everything else is fetched
  // on demand the first time the user reaches the field that needs it, and cached
  // after that (see ensure*Loaded helpers below) so it's only ever fetched once.
  useEffect(() => {
    let isMounted = true;

    async function fetchMajorClassifications() {
      try {
        const response = await graphqlQuery(allReportsMajorClassifications, setupManagementUrl.uri);
        if (isMounted) {
          setMajor(Array.isArray(response) ? response : []);
        }
      } catch (error) {
        console.error("Error fetching major classifications:", error);
      } finally {
        if (isMounted) {
          setMajorLoading(false);
        }
      }
    }

    fetchMajorClassifications();

    return () => {
      isMounted = false;
    };
  }, []);

  const ensureMinorLoaded = async () => {
    if (minorLoaded || minorLoading) {
      return minor;
    }

    setMinorLoading(true);
    try {
      const response = await graphqlQuery(allReportMinorClassifications, setupManagementUrl.uri);
      const data = Array.isArray(response) ? response : [];
      setMinor(data);
      setMinorLoaded(true);
      return data;
    } catch (error) {
      console.error("Error fetching minor classifications:", error);
      return [];
    } finally {
      setMinorLoading(false);
    }
  };

  const ensureReportTypesLoaded = async () => {
    if (reportTypesLoaded || reportTypesLoading) {
      return reportTypes;
    }

    setReportTypesLoading(true);
    try {
      const response = await graphqlQuery(allReportTypes, setupManagementUrl.uri);
      const data = Array.isArray(response) ? response : [];
      setReportTypes(data);
      setReportTypesLoaded(true);
      return data;
    } catch (error) {
      console.error("Error fetching report types:", error);
      return [];
    } finally {
      setReportTypesLoading(false);
    }
  };

  const ensureClientsLoaded = async () => {
    if (clientsLoaded || clientsLoading) {
      return clients;
    }

    setClientsLoading(true);
    try {
      const [individualResult, corporateResult] = await Promise.allSettled([
        fetchDataEngine(
          "allIndividualClients",
          "clientModel",
          { isActive: 1 },
          ["id", "fullName"],
          clientManagementUrl.uri
        ),
        fetchDataEngine(
          "allCorporateClients",
          "clientModel",
          { isActive: 1 },
          ["id", "companyName"],
          clientManagementUrl.uri
        ),
      ]);

      const individualData =
        individualResult.status === "fulfilled" && Array.isArray(individualResult.value)
          ? individualResult.value
          : [];
      const corporateData =
        corporateResult.status === "fulfilled" && Array.isArray(corporateResult.value)
          ? corporateResult.value
          : [];

      const individuals = individualData.map((client) => ({
        id: client.id,
        name: client.fullName,
        type: "Individual",
      }));
      const corporates = corporateData.map((client) => ({
        id: client.id,
        name: client.companyName,
        type: "Corporate",
      }));

      const mergedClients = [...individuals, ...corporates].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );

      setClients(mergedClients);
      setClientsLoaded(true);
      return mergedClients;
    } catch (error) {
      console.error("Error fetching clients:", error);
      return [];
    } finally {
      setClientsLoading(false);
    }
  };

  const ensurePayrollRunsLoaded = async () => {
    if (payrollRunsLoaded || payrollRunsLoading) {
      return payrollRuns;
    }

    setPayrollRunsLoading(true);
    try {
      const runs = await fetchPayrollRunsWithFallback();
      setPayrollRuns(runs);
      setPayrollRunsLoaded(true);
      return runs;
    } catch (error) {
      console.warn("Unable to load payroll runs for report browser:", error);
      return [];
    } finally {
      setPayrollRunsLoading(false);
    }
  };

  const majorOptions = useMemo(
    () =>
      major.map((item) => ({
        value: String(item.id),
        label: item.majorClassification,
      })),
    [major]
  );

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: String(client.id),
        label: `${client.name} (${client.type})`,
      })),
    [clients]
  );

  const payrollRunOptions = useMemo(
    () =>
      payrollRuns.map((run) => {
        const periodStart = formatDate(run.periodStart);
        const periodEnd = formatDate(run.periodEnd);
        const periodLabel =
          periodStart && periodEnd ? `${periodStart} - ${periodEnd}` : run.periodName || `Run #${run.id}`;

        return {
          value: String(run.id),
          label: `${run.periodName || `Run #${run.id}`} (${periodLabel})`,
          startDate: periodStart,
          endDate: periodEnd,
        };
      }),
    [payrollRuns]
  );

  const clientFilterOptions = (options, params) => {
    const { inputValue } = params;
    if (!inputValue.trim()) {
      return options.slice(0, 3);
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const resolveReportName = (reportTypeId) => {
    const dbReport = reportTypes.find((item) => String(item.id) === String(reportTypeId));
    if (dbReport?.reportName) {
      return dbReport.reportName;
    }

    const optionReport = filteredReportTypesOptions.find(
      (option) => String(option.value) === String(reportTypeId)
    );
    return optionReport?.label || "";
  };

  const initialValues = {
    majorClassificationID: "",
    minorClassificationID: "",
    reportTypeID: "",
    clientID: "",
    payrollRunId: "",
    startDate: "",
    endDate: "",
  };

  const validationSchema = yup.object().shape({
    majorClassificationID: yup.string().required("Major Classification is required"),
    minorClassificationID: yup.string().required("Minor Classification is required"),
    reportTypeID: yup.string().required("Report Type is required"),
    payrollRunId: yup.string().when("reportTypeID", {
      is: (value) => {
        const reportName = resolveReportName(value);
        return Boolean(getPayrollReportDefinition(reportName));
      },
      then: yup.string().required("Payroll Run is required"),
    }),
    startDate: yup.string().when("reportTypeID", {
      is: (value) => Number.parseInt(value, 10) === 1014,
      then: yup.string().required("Start Date is required"),
    }),
    clientID: yup.string().when("reportTypeID", {
      is: (value) => isFileProgressReportName(resolveReportName(value)),
      then: yup.string().required("Client is required for File Progress report"),
    }),
  });

  // Now async: each branch lazily loads whatever data it needs, instead of
  // relying on everything already being in memory from a page-load fetch.
  const handleFieldChange = async (fieldName, value, setFieldValue) => {
    if (fieldName === "majorClassificationID") {
      setFilteredReportTypesOptions([]);
      setFieldValue("minorClassificationID", "");
      setFieldValue("reportTypeID", "");
      setFieldValue("clientID", "");
      setFieldValue("payrollRunId", "");
      setFieldValue("startDate", "");
      setFieldValue("endDate", "");

      const minorData = await ensureMinorLoaded();
      const filteredMinor = minorData
        .filter((item) => String(item.majorClassificationID) === String(value))
        .map((item) => ({
          value: String(item.id),
          label: item.minorClassification,
        }));

      setFilteredMinorOptions(filteredMinor);
      return;
    }

    if (fieldName === "minorClassificationID") {
      setFieldValue("reportTypeID", "");
      setFieldValue("clientID", "");
      setFieldValue("payrollRunId", "");
      setFieldValue("startDate", "");
      setFieldValue("endDate", "");

      const reportTypeData = await ensureReportTypesLoaded();
      const filteredReportTypes = reportTypeData
        .filter((item) => String(item.minorClassificationID) === String(value))
        .map((item) => ({
          value: String(item.id),
          label: item.reportName,
        }));

      setFilteredReportTypesOptions(filteredReportTypes);
      return;
    }

    if (fieldName === "reportTypeID") {
      const reportName = resolveReportName(value);
      const isCustomPayrollReport = Boolean(getPayrollReportDefinition(reportName));
      const isLegacyPayrollReport = Number.parseInt(value, 10) === 1014;
      const needsClients = isFileProgressReportName(reportName);

      if (!needsClients) {
        setFieldValue("clientID", "");
      } else {
        ensureClientsLoaded();
      }

      setFieldValue("payrollRunId", "");

      if (isCustomPayrollReport) {
        ensurePayrollRunsLoaded();
      } else if (!isLegacyPayrollReport) {
        setFieldValue("startDate", "");
        setFieldValue("endDate", "");
      }
      return;
    }

    if (fieldName === "payrollRunId") {
      const selectedRun = payrollRunOptions.find((option) => String(option.value) === String(value));
      if (selectedRun) {
        setFieldValue("startDate", selectedRun.startDate || "");
        setFieldValue("endDate", selectedRun.endDate || "");
      }
      return;
    }

    if (fieldName === "startDate") {
      const lastDayOfMonth = getLastDayOfMonth(value);
      if (lastDayOfMonth) {
        setFieldValue("endDate", lastDayOfMonth);
      }
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    const reportName = resolveReportName(values.reportTypeID);
    const payrollReportDefinition = getPayrollReportDefinition(reportName);
    const fileStatusReportDefinition = getFileStatusReportDefinition(reportName);
    const financeReportDefinition = getFinanceReportDefinition(reportName);
    const selectedRun = payrollRunOptions.find(
      (option) => String(option.value) === String(values.payrollRunId)
    );
    const reportTab = openLoadingReportTab();

    if (!reportTab) {
      Swal.fire({
        icon: "error",
        title: "Popup blocked",
        text: "Allow popups for this site to view the generated report.",
      });
      setSubmitting(false);
      setIsSubmitting(false);
      return;
    }

    try {
      if (payrollReportDefinition) {
        const selectedPayrollRunId = Number.parseInt(values.payrollRunId, 10);
        let rows = [];

        try {
          const query = buildPayrollReportQuery(payrollReportDefinition, {
            payrollRunId: selectedPayrollRunId,
          });
          const reportRows = await queryPayrollDataWithFallback(query);
          rows = Array.isArray(reportRows) ? reportRows : [];
        } catch (payrollReportQueryError) {
          console.warn(
            "Payroll report query failed. Falling back to payroll line aggregation.",
            payrollReportQueryError
          );

          rows = await buildPayrollReportRowsFromPayrollLines(
            payrollReportDefinition,
            selectedPayrollRunId
          );
        }

        const filteredRows = Number.isNaN(selectedPayrollRunId)
          ? rows
          : rows.filter((row) => Number(row.payrollRunId) === selectedPayrollRunId);

        const html = buildPayrollReportHtml(payrollReportDefinition, filteredRows, {
          reportName,
          payrollRunLabel: selectedRun?.label || "",
          startDate: values.startDate,
          endDate: values.endDate,
        });

        reportTab.document.open();
        reportTab.document.write(html);
        reportTab.document.close();
        return;
      }

      if (fileStatusReportDefinition) {
        try {
          const rows = await buildFileStatusReportRows(fileStatusReportDefinition, {
            startDate: values.startDate,
            endDate: values.endDate,
            clientId: values.clientID,
          });

          const html = buildPayrollReportHtml(fileStatusReportDefinition, rows, {
            reportName,
            startDate: values.startDate,
            endDate: values.endDate,
          });

          reportTab.document.open();
          reportTab.document.write(html);
          reportTab.document.close();
          return;
        } catch (fileStatusError) {
          console.warn(
            "File status custom report fetch failed. Falling back to report engine.",
            fileStatusError
          );
        }
      }

      if (financeReportDefinition) {
        try {
          const rows = await buildFinanceReportRows(financeReportDefinition);

          const html = buildPayrollReportHtml(financeReportDefinition, rows, {
            reportName,
            startDate: values.startDate,
            endDate: values.endDate,
          });

          reportTab.document.open();
          reportTab.document.write(html);
          reportTab.document.close();
          return;
        } catch (financeReportError) {
          console.warn(
            "Finance custom report fetch failed. Falling back to report engine.",
            financeReportError
          );
        }
      }

      const payload = {
        ReportTypeId: Number.parseInt(values.reportTypeID, 10),
        ReportType: reportName,
        StartDate: values.startDate,
        EndDate: values.endDate,
        ClientId: values.clientID ? Number.parseInt(values.clientID, 10) : null,
        CompanyId: companyId,
        UserId: userId,
      };

      const response = await generateReport(
        payload,
        reportManagementUrl.generate_report_uri,
        setIsSubmitting
      );

      if (!response || response.ok !== true) {
        const statusText = response?.status ? `Status: ${response.status}` : "";
        const errorMessage =
          response?.message || `Failed to generate report.${statusText ? ` ${statusText}` : ""}`;
        throw new Error(errorMessage.trim());
      }

      const htmlContent = await response.text();
      const enhancedHtmlContent = addReportActionsToHtml(htmlContent, reportName);
      reportTab.document.open();
      reportTab.document.write(enhancedHtmlContent);
      reportTab.document.close();
    } catch (error) {
      console.error("Error generating report:", error);
      reportTab.close();
      Swal.fire({
        icon: "error",
        title: "Error",
        text: resolveErrorMessage(error, "Failed to generate report. Please try again."),
      });
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  // No more page-level loading gate — the form renders immediately.
  return (
    <Card sx={{ p: 3, background: `${colors.primary[400]} !important` }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
          Report Browser
        </Typography>
        <Typography variant="body1" color={colors.grey[400]}>
          Please select the report parameters
        </Typography>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          isSubmitting: formikSubmitting,
        }) => {
          const selectedReportName = resolveReportName(values.reportTypeID);
          const isFileProgress = isFileProgressReportName(selectedReportName);
          const isCustomPayrollReport = Boolean(getPayrollReportDefinition(selectedReportName));
          const isLegacyPayrollReport = Number.parseInt(values.reportTypeID, 10) === 1014;

          return (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <FormControl
                    fullWidth
                    error={touched.majorClassificationID && Boolean(errors.majorClassificationID)}
                    disabled={majorLoading}
                  >
                    <InputLabel>Major Classification *</InputLabel>
                    <Select
                      name="majorClassificationID"
                      value={values.majorClassificationID}
                      onChange={(event) => {
                        const value = event.target.value;
                        setFieldValue("majorClassificationID", String(value));
                        handleFieldChange("majorClassificationID", value, setFieldValue);
                      }}
                      onBlur={() => setFieldTouched("majorClassificationID", true)}
                      label="Major Classification *"
                      endAdornment={majorLoading ? <CircularProgress size={18} sx={{ mr: 2 }} /> : null}
                    >
                      <MenuItem value="">
                        <em>{majorLoading ? "Loading..." : "Select Major Classification"}</em>
                      </MenuItem>
                      {majorOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <ErrorMessage name="majorClassificationID">
                      {(message) => (
                        <Typography color="error" variant="caption">
                          {message}
                        </Typography>
                      )}
                    </ErrorMessage>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl
                    fullWidth
                    error={touched.minorClassificationID && Boolean(errors.minorClassificationID)}
                    disabled={!values.majorClassificationID || minorLoading}
                  >
                    <InputLabel>Minor Classification *</InputLabel>
                    <Select
                      name="minorClassificationID"
                      value={values.minorClassificationID}
                      onChange={(event) => {
                        const value = event.target.value;
                        setFieldValue("minorClassificationID", String(value));
                        handleFieldChange("minorClassificationID", value, setFieldValue);
                      }}
                      onBlur={() => setFieldTouched("minorClassificationID", true)}
                      label="Minor Classification *"
                      endAdornment={minorLoading ? <CircularProgress size={18} sx={{ mr: 2 }} /> : null}
                    >
                      <MenuItem value="">
                        <em>{minorLoading ? "Loading..." : "Select Minor Classification"}</em>
                      </MenuItem>
                      {filteredMinorOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <ErrorMessage name="minorClassificationID">
                      {(message) => (
                        <Typography color="error" variant="caption">
                          {message}
                        </Typography>
                      )}
                    </ErrorMessage>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl
                    fullWidth
                    error={touched.reportTypeID && Boolean(errors.reportTypeID)}
                    disabled={!values.minorClassificationID || reportTypesLoading}
                  >
                    <InputLabel>Report Type *</InputLabel>
                    <Select
                      name="reportTypeID"
                      value={values.reportTypeID}
                      onChange={(event) => {
                        const value = event.target.value;
                        setFieldValue("reportTypeID", String(value));
                        handleFieldChange("reportTypeID", value, setFieldValue);
                      }}
                      onBlur={() => setFieldTouched("reportTypeID", true)}
                      label="Report Type *"
                      endAdornment={reportTypesLoading ? <CircularProgress size={18} sx={{ mr: 2 }} /> : null}
                    >
                      <MenuItem value="">
                        <em>{reportTypesLoading ? "Loading..." : "Select Report Type"}</em>
                      </MenuItem>
                      {filteredReportTypesOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <ErrorMessage name="reportTypeID">
                      {(message) => (
                        <Typography color="error" variant="caption">
                          {message}
                        </Typography>
                      )}
                    </ErrorMessage>
                  </FormControl>
                </Grid>

                {isCustomPayrollReport && (
                  <Grid item xs={12} md={3}>
                    <FormControl
                      fullWidth
                      error={touched.payrollRunId && Boolean(errors.payrollRunId)}
                      disabled={payrollRunsLoading}
                    >
                      <InputLabel>Payroll Run *</InputLabel>
                      <Select
                        name="payrollRunId"
                        value={values.payrollRunId}
                        label="Payroll Run *"
                        onChange={(event) => {
                          const value = event.target.value;
                          setFieldValue("payrollRunId", String(value));
                          handleFieldChange("payrollRunId", value, setFieldValue);
                        }}
                        onBlur={() => setFieldTouched("payrollRunId", true)}
                        endAdornment={payrollRunsLoading ? <CircularProgress size={18} sx={{ mr: 2 }} /> : null}
                      >
                        <MenuItem value="">
                          <em>{payrollRunsLoading ? "Loading..." : "Select Payroll Run"}</em>
                        </MenuItem>
                        {payrollRunOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <ErrorMessage name="payrollRunId">
                        {(message) => (
                          <Typography color="error" variant="caption">
                            {message}
                          </Typography>
                        )}
                      </ErrorMessage>
                    </FormControl>
                  </Grid>
                )}

                {isFileProgress && (
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth error={touched.clientID && Boolean(errors.clientID)}>
                      <Autocomplete
                        options={clientOptions}
                        loading={clientsLoading}
                        filterOptions={clientFilterOptions}
                        getOptionLabel={(option) => option.label}
                        value={
                          clientOptions.find(
                            (option) => String(option.value) === String(values.clientID)
                          ) || null
                        }
                        onChange={(_, selectedOption) => {
                          setFieldValue("clientID", selectedOption?.value || "");
                          setFieldTouched("clientID", true);
                        }}
                        onBlur={() => setFieldTouched("clientID", true)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Client *"
                            error={touched.clientID && Boolean(errors.clientID)}
                            helperText={touched.clientID && errors.clientID}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {clientsLoading ? <CircularProgress size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    name="startDate"
                    value={values.startDate}
                    onChange={(event) => {
                      setFieldValue("startDate", event.target.value);
                      if (isLegacyPayrollReport) {
                        handleFieldChange("startDate", event.target.value, setFieldValue);
                      }
                    }}
                    onBlur={() => setFieldTouched("startDate", true)}
                    error={touched.startDate && Boolean(errors.startDate)}
                    helperText={
                      (touched.startDate && errors.startDate) ||
                      (isCustomPayrollReport
                        ? "Auto-populated from selected payroll run"
                        : isLegacyPayrollReport
                        ? "Select payroll month"
                        : "Optional")
                    }
                    disabled={isCustomPayrollReport}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    name="endDate"
                    value={values.endDate}
                    onChange={(event) => setFieldValue("endDate", event.target.value)}
                    onBlur={() => setFieldTouched("endDate", true)}
                    helperText={
                      isCustomPayrollReport
                        ? "Auto-populated from selected payroll run"
                        : isLegacyPayrollReport
                        ? "Auto-calculated from selected month"
                        : "Optional"
                    }
                    disabled={isLegacyPayrollReport || isCustomPayrollReport}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Box mt={4} display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={isSubmitting || formikSubmitting}
                  startIcon={isSubmitting || formikSubmitting ? <CircularProgress size={20} /> : null}
                >
                  {isSubmitting || formikSubmitting ? "Generating Report..." : "Generate Report"}
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Card>
  );
};

export default Filter;